import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { Button, Card, Icon, Notice } from '../../../ui';
import type { InventoryCategory } from '../../../types/inventory';
import { INVENTORY_CATEGORY_OPTIONS } from '../../../utils/inventoryConstants';
import { formatPartNumber } from '../../../functions/formatPartNumber';
import { FieldBadge } from './FieldLabelWithBadge';
import type { AddItemDetailsVariant } from './types';

const categoryLabel = (category: InventoryCategory) =>
    INVENTORY_CATEGORY_OPTIONS.find((opt) => opt.value === category)?.label ?? category;

const PartNumberLine = ({
    partNumber,
    category,
}: {
    partNumber: string;
    category: InventoryCategory;
}) => {
    const stripped = formatPartNumber(partNumber);
    return (
        <Flex align="center" gap={2} minW={0}>
            {stripped ? (
                <Text fontFamily="mono" fontSize="md" fontWeight="semibold" color="fg.default" noOfLines={1}>
                    {stripped}
                </Text>
            ) : (
                <Text fontSize="md" color="fg.muted">
                    No part number yet
                </Text>
            )}
            <FieldBadge tone="neutral">{categoryLabel(category)}</FieldBadge>
        </Flex>
    );
};

interface PartNumberCardProps {
    partNumber: string;
    category: InventoryCategory;
    onChange: () => void;
    changeLabel?: string;
}

/** Compact "Part number" summary with a link back to the Identify step. */
export const PartNumberCard = ({
    partNumber,
    category,
    onChange,
    changeLabel = 'Change',
}: PartNumberCardProps) => (
    <Card variant="outline" px={4} py={2.5}>
        <Flex align="center" justify="space-between" gap={3}>
            <Box minW={0}>
                <Text textStyle="caption" color="fg.muted">
                    Part number
                </Text>
                <PartNumberLine partNumber={partNumber} category={category} />
            </Box>
            <Button variant="link" size="sm" flexShrink={0} onClick={onChange}>
                {changeLabel}
            </Button>
        </Flex>
    </Card>
);

interface PartSummaryCardProps {
    variant: AddItemDetailsVariant;
    partNumber: string;
    category: InventoryCategory;
    /** Equivalent part numbers the catalog returned, minus the primary. */
    alsoMatches: Array<string>;
    showMotorNotice: boolean;
    existingItemId: string | null;
    duplicateItemId: string | null;
    onChange: () => void;
    onViewItem: (itemId: string) => void;
}

const ViewItemButton = ({ onClick }: { onClick: () => void }) => (
    <Button size="sm" variant="outline" flexShrink={0} onClick={onClick}>
        View Item
    </Button>
);

const PartSummaryCard = ({
    variant,
    partNumber,
    category,
    alsoMatches,
    showMotorNotice,
    existingItemId,
    duplicateItemId,
    onChange,
    onViewItem,
}: PartSummaryCardProps) => (
    <Stack spacing={3}>
        {variant === 'match' ? (
            <Card variant="outline" px={4} py={3.5}>
                <Flex align="flex-start" justify="space-between" gap={3}>
                    <Box minW={0}>
                        <PartNumberLine partNumber={partNumber} category={category} />
                        <Flex align="center" gap={1.5} mt={1} color="brand.fg">
                            <Icon name="checkCircle" size="sm" />
                            <Text fontSize="xs" fontWeight="semibold">
                                Matched in the parts catalog
                            </Text>
                        </Flex>
                    </Box>
                    <Button variant="link" size="sm" flexShrink={0} onClick={onChange}>
                        Change
                    </Button>
                </Flex>
                {alsoMatches.length > 0 && (
                    <Flex align="center" gap={2} flexWrap="wrap" mt={3}>
                        <Text textStyle="caption" color="fg.muted">
                            Also matches
                        </Text>
                        {alsoMatches.map((pn) => (
                            <Text
                                key={pn}
                                as="span"
                                fontFamily="mono"
                                fontSize="xs"
                                fontWeight="medium"
                                color="fg.default"
                                bg="bg.subtle"
                                borderRadius="md"
                                px={2}
                                py={0.5}
                            >
                                {pn}
                            </Text>
                        ))}
                    </Flex>
                )}
                {showMotorNotice && (
                    <Text textStyle="caption" color="fg.muted" mt={3}>
                        Part data provided by MOTOR.
                    </Text>
                )}
            </Card>
        ) : (
            <PartNumberCard
                partNumber={partNumber}
                category={category}
                onChange={onChange}
                changeLabel={variant === 'existing' ? 'Change' : 'Edit'}
            />
        )}

        {variant === 'noMatch' && (
            <Notice tone="warning" title="No match in the parts catalog or your inventory.">
                Check the number for a typo (a G where a 6 belongs is common), or fill in the
                details below. Nothing was prefilled, so every field is yours.
                <Box as="span" display="block" mt={2}>
                    <Button variant="link" size="sm" onClick={onChange}>
                        Fix the number and retry
                    </Button>
                </Box>
            </Notice>
        )}

        {duplicateItemId ? (
            <Notice
                tone="info"
                action={<ViewItemButton onClick={() => onViewItem(duplicateItemId)} />}
            >
                An item with the same location, supplier, and category already exists for this
                part number.
            </Notice>
        ) : (
            existingItemId && (
                <Notice
                    tone="info"
                    title="This part is already in inventory."
                    action={<ViewItemButton onClick={() => onViewItem(existingItemId)} />}
                />
            )
        )}
    </Stack>
);

export default PartSummaryCard;
